import { Injectable } from '@angular/core';
import { Observable, Observer, ReplaySubject, Subject, forkJoin, from, of } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';

const DB_OPERATIONS = {
  GET: 'get',
  PUT: 'put',
  DELETE: 'delete',
};

// List of Angular state properties that should be persisted in IndexedDB
// This list is used to determine which parts of the Angular state should be saved in IndexedDB
const listOfAngularStateToBePersisted = ['cart.cartResponse', 'billBlockInfo', 'bscsCustomerID'];

type DbOperation = (typeof DB_OPERATIONS)[keyof typeof DB_OPERATIONS];

@Injectable({
  providedIn: 'root',
})
export class PhoenixStateService {
  phoenixStateDb$: Subject<IDBDatabase> = new ReplaySubject<IDBDatabase>(1);
  dbName = 'phx-state-service';

  initializeDatabase(storeName): void {
    if (!window.indexedDB) {
      this.phoenixStateDb$.error(new Error('IndexedDB is not supported'));
      this.phoenixStateDb$.complete();
      return;
    }
    const openRequest = indexedDB.open(this.dbName);
    openRequest.onerror = (err) => {
      this.phoenixStateDb$.error(err);
      this.phoenixStateDb$.complete();
    };
    openRequest.onupgradeneeded = (e: any) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(storeName)) {
        db.createObjectStore(storeName);
      }
    };
    openRequest.onsuccess = (e: any) => {
      const db: IDBDatabase = e.target.result;
      this.phoenixStateDb$.next(db);
    };
  }

  get(storeName: string, key: any): Observable<any> {
    this.initializeDatabase(storeName);
    return this.phoenixStateDb$.pipe(
      take(1),
      switchMap((db) => {
        if (!db) {
          return of(false);
        }
        return from(this.accessStore(db, DB_OPERATIONS.GET, storeName, key));
      }),
      catchError((err) => {
        console.error('Error accessing IndexedDB:', err);
        return of(undefined);
      })
    );
  }

  set(storeName: string, key: any, value: any): Observable<boolean> {
    this.initializeDatabase(storeName);
    return this.phoenixStateDb$.pipe(
      take(1),
      switchMap((db) => {
        if (!db) {
          return of(false);
        }
        return from(this.accessStore(db, DB_OPERATIONS.PUT, storeName, key, value));
      }),
      catchError((err) => {
        console.error('Error accessing IndexedDB:', err);
        return of(false);
      })
    );
  }

  delete(storeName: string, key: any): Observable<boolean> {
    this.initializeDatabase(storeName);
    return this.phoenixStateDb$.pipe(
      take(1),
      switchMap((db) => {
        if (!db) {
          return of(false);
        }
        return from(this.accessStore(db, DB_OPERATIONS.DELETE, storeName, key));
      }),
      catchError((err) => {
        console.error('Error accessing IndexedDB:', err);
        return of(false);
      })
    );
  }

  setMultipleValues(storeName: string, datas: { key: any; value: any }[]): Observable<any> {
    return forkJoin(datas.map((data) => this.set(storeName, data.key, data.value)));
  }

  deleteDB() {
    return new Observable((observer: Observer<any>) => {
      if (!window.indexedDB) {
        observer.error(`IndexedDB is not supported in this environment`);
        return;
      }
      const request = indexedDB.deleteDatabase(this.dbName);
      request.onsuccess = () => {
        observer.next(`Database "${this.dbName}" deleted successfully`);
        observer.complete();
      };
      request.onerror = (e: any) => {
        observer.error(
          `Failed to delete the database "${this.dbName}" with error ${e.target?.error || 'Unknown error'}`
        );
        observer.complete();
      };
      request.onblocked = () => {
        observer.error(
          `Database deletion blocked. Ensure all connections to ${this.dbName} are closed`
        );
        observer.complete();
      };
    });
  }

  accessStore(
    db: IDBDatabase,
    operation: DbOperation,
    storeName: string,
    key: any,
    value?: any
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const operationMode =
        operation === DB_OPERATIONS.PUT || operation === DB_OPERATIONS.DELETE
          ? 'readwrite'
          : 'readonly';
      const txn = db.transaction([storeName], operationMode);
      const store = txn.objectStore(storeName);
      const req = operation !== DB_OPERATIONS.PUT ? store[operation](key) : store.put(value, key);

      req.onerror = (e: any) => {
        reject(e.target?.error);
      };
      req.onsuccess = (e: any) => {
        resolve(e.target.result);
      };
    });
  }
  // TODO: we are currently handling only the keyValueStore ,
  // we can enhance this to handle ttlStores and singleUseStores when we have the requirement
  setAngularStateToBePersisted(path: string, value: any): void {
    const angularStatePath = listOfAngularStateToBePersisted.find((statePath) =>
      path.includes(statePath)
    );
    if (angularStatePath) {
      // Observables are cold , so must be subscribed to trigger the execution
      // This will ensure that the value is set in IndexedDB
      this.set('StateService', angularStatePath, value).subscribe(() => {});
    }
  }
}
